import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useAuth } from '@/context/AuthContext';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import { isDesktopOffline } from '@/api/desktopSync';
import { getPushSubscriptionStatus, registerPushSubscription } from '@/api/notifications';
import { toast } from 'sonner';

const urlBase64ToUint8Array = value => {
  const base64 = (value + '='.repeat((4 - value.length % 4) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(window.atob(base64), character => character.charCodeAt(0));
};
const browserPermission = () => typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
const subscriptionPayload = subscription => {
  const { endpoint, keys } = subscription.toJSON();
  return { endpoint, keys }; // expirationTime is browser metadata, not our registration contract.
};
const getWorkerRegistration = () => new Promise((resolve, reject) => {
  const timer = setTimeout(() => reject(new Error('worker_unavailable')), 5000);
  Promise.resolve().then(() => navigator.serviceWorker.getRegistration()).then(resolve, reject).finally(() => clearTimeout(timer));
});

// Browser subscription existence is not registration for the current account.
// Every continuation belongs to one cloud session and one mounted generation.
export default function usePushNotifications() {
  const { sessionBinding, getToken } = useAuth() || {};
  const revision = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  const supported = typeof Notification !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;
  const offline = isDesktopOffline();
  const key = JSON.stringify([sessionBinding?.user?.tenant_id, sessionBinding?.user?.id, sessionBinding?.source, sessionBinding?.revision, revision, offline, supported]);
  const eligible = !!sessionBinding?.user?.id && !!sessionBinding?.user?.tenant_id
    && sessionBinding.source === 'cloud' && sessionBinding.revision === revision && !offline;
  const current = useRef(null);
  current.current = { key, eligible, getToken };
  const owner = useRef(null);
  const [state, setState] = useState(null);
  const initialStatus = !supported ? 'unsupported' : !eligible ? 'offline' : browserPermission() === 'denied' ? 'denied' : 'checking';
  const visible = state?.key === key ? state : { status: initialStatus, pending: eligible && supported, permission: browserPermission(), message: '' };

  const owns = cycle => owner.current === cycle && cycle.active && current.current.key === cycle.key
    && current.current.eligible && getActiveSessionRevision() === cycle.revision && !isDesktopOffline();
  const publish = (cycle, patch) => {
    if (owns(cycle)) setState(previous => ({ ...(previous?.key === cycle.key ? previous : {}), key: cycle.key, ...patch }));
  };
  const begin = cycle => {
    if (!cycle || !owns(cycle) || cycle.busy) return null;
    const token = current.current.getToken?.();
    if (!owns(cycle)) return null;
    if (!token) {
      cycle.status = 'unverified';
      publish(cycle, { status: 'unverified', pending: false, message: 'Sign in again to check notification registration.' });
      return null;
    }
    cycle.busy = true;
    publish(cycle, { pending: true, message: '' });
    return { headers: { Authorization: `Bearer ${token}` }, timeout: 10000, suppressServerErrorNotification: true };
  };
  const finish = cycle => {
    cycle.busy = false;
    publish(cycle, { pending: false });
  };
  const registration = async cycle => {
    // ready can wait forever when no worker controls this origin. A missing or
    // installing worker is an explicit retryable state instead of a hung UI.
    const reg = await getWorkerRegistration();
    if (!owns(cycle)) return null;
    if (!reg?.active || !reg.pushManager) throw new Error('worker_unavailable');
    return reg;
  };
  const check = async (cycle, subscription, config) => {
    const controller = new AbortController();
    cycle.controllers.add(controller);
    try {
      const result = await getPushSubscriptionStatus(subscriptionPayload(subscription), { ...config, signal: controller.signal, timeout: 10000 });
      if (!owns(cycle)) return null;
      if (typeof result?.registered !== 'boolean') throw new Error('invalid_status');
      cycle.uncertain = false;
      cycle.status = result.registered ? 'registered' : 'unregistered';
      publish(cycle, { status: cycle.status, message: '' });
      return result.registered;
    } finally { cycle.controllers.delete(controller); }
  };
  const refresh = async cycle => {
    const config = begin(cycle);
    if (!config) return;
    try {
      if (browserPermission() === 'denied') {
        cycle.status = 'denied';
        publish(cycle, { status: 'denied', permission: 'denied', message: 'Notifications are blocked by this browser. Check site settings.' });
        return;
      }
      const reg = await registration(cycle);
      if (!reg || !owns(cycle)) return;
      const sub = await reg.pushManager.getSubscription();
      if (!owns(cycle)) return;
      publish(cycle, { permission: browserPermission() });
      if (sub) await check(cycle, sub, config);
      else {
        cycle.uncertain = false;
        cycle.status = 'unregistered';
        publish(cycle, { status: browserPermission() === 'denied' ? 'denied' : 'unregistered', message: '' });
      }
    } catch {
      cycle.status = 'unverified';
      publish(cycle, { status: 'unverified', message: 'Notification registration could not be checked. Check again when this browser and server are available.' });
    } finally { finish(cycle); }
  };

  useEffect(() => {
    const cycle = { key, revision, active: true, busy: false, uncertain: false, status: initialStatus, controllers: new Set() };
    owner.current = cycle;
    setState({ key, status: initialStatus, pending: false, permission: browserPermission(), message: '' });
    if (eligible && supported && initialStatus !== 'denied') refresh(cycle);
    return () => {
      cycle.active = false;
      cycle.controllers.forEach(controller => controller.abort());
    };
    // The primitive key contains the complete session/online scope. Callback
    // identities are intentionally not lifecycle events.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const subscribe = async () => {
    const cycle = owner.current;
    // Unverified/unknown results must be checked, never silently retried writes.
    if (!supported || !cycle || cycle.status !== 'unregistered' || cycle.uncertain) return false;
    const config = begin(cycle);
    if (!config) return false;
    try {
      const vapid = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      if (!vapid) {
        publish(cycle, { message: 'Notification setup is unavailable: the browser push key is not configured.' });
        return false;
      }
      const permission = await Notification.requestPermission();
      if (!owns(cycle)) return false;
      publish(cycle, { permission });
      if (permission !== 'granted') {
        cycle.status = permission === 'denied' ? 'denied' : 'unregistered';
        publish(cycle, { status: permission === 'denied' ? 'denied' : 'unregistered', message: permission === 'denied'
          ? 'Notifications are blocked by this browser. Check site settings.' : 'Notification permission was not granted. You can try again.' });
        return false;
      }
      const reg = await registration(cycle);
      if (!reg || !owns(cycle)) return false;
      let subscription = await reg.pushManager.getSubscription();
      if (!owns(cycle)) return false;
      if (!subscription) {
        subscription = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(vapid) });
        if (!owns(cycle)) return false;
      }
      try { await registerPushSubscription(subscriptionPayload(subscription), config); }
      catch (error) {
        if (!owns(cycle)) return false;
        const status = error?.response?.status;
        if (!status || status === 408 || status >= 500) {
          cycle.uncertain = true;
          cycle.status = 'unverified';
          publish(cycle, { status: 'unverified', message: 'Registration result is not confirmed. Checking its status before another attempt.' });
          try {
            const registered = await check(cycle, subscription, config);
            if (registered && owns(cycle)) toast.success('Notifications registered for this account.');
            return !!registered && owns(cycle);
          } catch {
            publish(cycle, { status: 'unverified', message: 'Registration may have succeeded. Check status before trying to enable again.' });
            return false;
          }
        }
        publish(cycle, { message: 'Notification registration was rejected. You can try again.' });
        return false;
      }
      if (!owns(cycle)) return false;
      cycle.status = 'registered';
      publish(cycle, { status: 'registered', message: '' });
      toast.success('Notifications registered for this account.');
      return true;
    } catch {
      publish(cycle, { message: 'This browser could not finish notification setup. Check the browser and try again.' });
      return false;
    } finally { finish(cycle); }
  };
  return {
    supported, ...visible, subscribed: visible.status === 'registered', subscribe,
    canEnable: eligible && !visible.pending && visible.status === 'unregistered',
    retryStatus: () => refresh(owner.current),
  };
}
